import { API_BASE_URL } from '@/lib/constants';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/services/supabase';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  requireAuth?: boolean;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { requireAuth = true, ...fetchOptions } = options;
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((fetchOptions.headers as Record<string, string>) || {}),
    };

    // Add auth token if required and available (SSoT: auth store)
    if (requireAuth) {
      const token = useAuthStore.getState().session?.access_token;
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // 204 No Content (and any response with no body) — don't attempt to read the body
    let data: any = null;
    const contentLength = response.headers.get('content-length');
    const contentType = response.headers.get('content-type') || '';
    const hasBody =
      response.status !== 204 &&
      response.status !== 205 &&
      contentLength !== '0' &&
      contentLength !== null
        ? true
        : contentType.includes('application/json') || contentType.includes('text/');

    if (hasBody) {
      const text = await response.text();
      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }
      }
    }

    // On 401: attempt one token refresh, then retry
    if (response.status === 401 && requireAuth && !(options as any)._retried) {
      try {
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError && refreshData?.session) {
          // Update authStore with fresh session
          const refreshedUser = refreshData.session.user;
          useAuthStore.getState().setSession({
            access_token: refreshData.session.access_token,
            refresh_token: refreshData.session.refresh_token,
            expires_at: refreshData.session.expires_at || 0,
            user: {
              id: refreshedUser.id,
              email: refreshedUser.email || '',
              display_name:
                refreshedUser.user_metadata?.display_name || refreshedUser.user_metadata?.full_name,
              avatar_url: refreshedUser.user_metadata?.avatar_url,
              role: refreshedUser.user_metadata?.role === 'admin' ? 'admin' : 'user',
              created_at: refreshedUser.created_at,
              last_sign_in_at: refreshedUser.last_sign_in_at,
            },
          });
          // Retry the original request once
          return this.request<T>(endpoint, { ...options, _retried: true } as any);
        }
      } catch {
        // refresh failed — fall through to error
      }
      // If refresh failed, force logout
      useAuthStore.getState().setSession(null);
      useAuthStore.getState().setUser(null);
      throw new ApiError(401, 'Session expired. Please log in again.', data);
    }

    if (!response.ok) {
      const errorMsg = data?.error?.message?.[0] || data?.message || 'Request failed';
      throw new ApiError(response.status, errorMsg, data);
    }

    // Unwrap API envelope: { data: T, success: true, ... } → T
    if (data && typeof data === 'object' && 'data' in data && 'success' in data) {
      return data.data;
    }

    return data;
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Create and export the default API client instance
export const api = new ApiClient(API_BASE_URL);

// Bracket-specific API methods
export const bracketApi = {
  createBracket: (data: { name: string; tournamentId: string; description?: string }) =>
    api.post<any>('/brackets', data),

  getBrackets: () => api.get<any[]>('/brackets'),

  getUserBrackets: () => api.get<any[]>('/brackets'),

  getBracket: (id: string) => api.get<any>(`/brackets/${id}`),

  updatePick: (
    bracketId: string,
    data: {
      gameId: string;
      teamId: string;
      confidence?: number;
    }
  ) => api.patch<any>(`/brackets/${bracketId}/picks`, data),

  submitBracket: (bracketId: string, data?: { finalComments?: string }) =>
    api.post<any>(`/brackets/${bracketId}/submit`, data),

  deleteBracket: (id: string) => api.delete<any>(`/brackets/${id}`),

  compareBrackets: (id1: string, id2: string) => api.get<any>(`/brackets/${id1}/compare/${id2}`),
};

// Tournament data API methods
export const tournamentApi = {
  getTeams: () => api.get<any[]>('/tournaments/teams'),
  getGames: () => api.get<any[]>('/tournaments/games'),
  getTournament: (year?: number) =>
    api.get<any>(`/tournaments/${year || new Date().getFullYear()}`),
};

// Teams API methods
export const teamsApi = {
  getTeams: (filters?: { conference?: string; region?: string; seeded?: boolean }) => {
    const params = new URLSearchParams();
    if (filters?.conference) params.set('conference', filters.conference);
    if (filters?.region) params.set('region', filters.region);
    if (filters?.seeded) params.set('seeded', 'true');
    const qs = params.toString();
    return api.get<any[]>(`/teams${qs ? `?${qs}` : ''}`, { requireAuth: false });
  },
  getTeam: (id: string) => api.get<any>(`/teams/${id}`, { requireAuth: false }),
};

// Leagues API methods
export const leaguesApi = {
  list: (filters?: { sport?: string }) => {
    const qs = filters?.sport ? `?sport=${encodeURIComponent(filters.sport)}` : '';
    return api.get<any>(`/leagues${qs}`);
  },
  discover: (filters?: { sport?: string }) => {
    const qs = filters?.sport ? `?sport=${encodeURIComponent(filters.sport)}` : '';
    return api.get<any>(`/leagues/discover${qs}`);
  },
  detail: (id: string) => api.get<any>(`/leagues/${id}`),
  create: (data: {
    name: string;
    description?: string;
    maxMembers?: number;
    privacy?: string;
    sport?: string;
  }) => api.post<any>('/leagues', data),
  join: (id: string, data: { inviteCode?: string }) => api.post<any>(`/leagues/${id}/join`, data),
  joinByCode: (data: { inviteCode: string }) => api.post<any>('/leagues/join-by-code', data),
  leave: (id: string) => api.post<any>(`/leagues/${id}/leave`),
  update: (
    id: string,
    data: { prizePool?: number; prizeNotes?: string; description?: string; name?: string }
  ) => api.patch<any>(`/leagues/${id}`, data),

  invitesList: (leagueId: string) => api.get<any>(`/leagues/${leagueId}/invites`),
  invitesCreate: (leagueId: string) => api.post<any>(`/leagues/${leagueId}/invites`, {}),
  invitesRevoke: (leagueId: string, inviteId: string) =>
    api.post<any>(`/leagues/${leagueId}/invites/${inviteId}/revoke`, {}),

  removeMember: (leagueId: string, userId: string) =>
    api.post<any>(`/leagues/${leagueId}/members/${userId}/remove`, {}),

  getScoringSettings: (leagueId: string) => api.get<any>(`/leagues/${leagueId}/scoring-settings`),
  updateScoringSettings: (
    leagueId: string,
    settings: {
      useUpsetBonus?: boolean;
      useStreakBonus?: boolean;
      useConfidencePoints?: boolean;
      roundWeights?: number[];
      upsetCap?: number;
      streakParams?: Record<string, any>;
    }
  ) => api.post<any>(`/leagues/${leagueId}/scoring-settings`, settings),

  recomputeScores: (leagueId: string) => api.post<any>(`/leagues/${leagueId}/recompute-scores`, {}),

  getMessages: (leagueId: string, gameId?: string | null, limit = 200) => {
    const qs = new URLSearchParams();
    if (gameId) qs.set('gameId', gameId);
    qs.set('limit', String(limit));
    return api.get<any>(`/leagues/${leagueId}/messages?${qs.toString()}`);
  },
  sendMessage: (leagueId: string, body: string, gameId?: string | null) =>
    api.post<any>(`/leagues/${leagueId}/messages`, { body, ...(gameId ? { gameId } : {}) }),
};

// Leaderboard API methods
export const leaderboardApi = {
  get: (params: {
    scope: 'global' | 'friends' | 'league';
    leagueId?: string;
    sort?: 'rank' | 'points' | 'accuracy';
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    qs.set('scope', params.scope);
    if (params.leagueId) qs.set('leagueId', params.leagueId);
    if (params.sort) qs.set('sort', params.sort);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    return api.get<any>(`/leaderboard?${qs.toString()}`);
  },
};

export const wcApi = {
  syncBracket: (data: {
    group_picks?: any[];
    groupPicks?: any[];
    ko_picks?: Record<string, number>;
    koPicks?: Record<string, number>;
    group_rankings?: Record<string, number[]> | null;
    groupRankings?: Record<string, number[]> | null;
    best_third_selections?: number[] | null;
    bestThirdSelections?: number[] | null;
    score_breakdown?: Record<string, any> | null;
    scoreBreakdown?: Record<string, any> | null;
  }) => api.post<any>('/wc/brackets', data),
  getMyBracket: () => api.get<any | null>('/wc/brackets/me'),
  getLeaderboard: (params?: { scope?: 'global' | 'sams-army' | string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.scope) qs.set('scope', params.scope);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return api.get<any>(`/wc/leaderboard${query ? `?${query}` : ''}`, { requireAuth: false });
  },
};

export const privacyApi = {
  getPreferences: () => api.get<any>('/privacy/preferences'),
  updatePreferences: (data: {
    emailUpdatesOptIn?: boolean;
    smsOptIn?: boolean;
    pushOptIn?: boolean;
    phone?: string | null;
    favoriteTeamId?: string | null;
    favoriteTeamName?: string | null;
    favoriteTeamCode?: string | null;
    policyVersion?: string;
    termsVersion?: string;
    source?: string | null;
    campaign?: string | null;
    accountTermsAccepted?: boolean;
    ageConfirmed?: boolean;
    consentText?: string;
    sourceUrl?: string;
  }) => api.patch<any>('/privacy/preferences', data),
};

// Simulation API methods
export const simApi = {
  status: () => api.get<any>('/sim/status'),
  run: (seed?: number) => api.post<any>('/sim/run', { seed }),
  advance: (count: number = 1, seed?: number) => api.post<any>('/sim/advance', { count, seed }),
  reset: () => api.post<any>('/sim/reset', {}),
  toggle: (enabled: boolean) => api.post<any>('/sim/toggle', { enabled }),
};

// Scout API methods
export const scoutApi = {
  getTeamBadges: (id: string, opts?: { debug?: boolean }) => {
    const qs = opts?.debug ? '?debug=1' : '';
    return api.get<any>(`/scout/teams/${id}/badges${qs}`);
  },

  /** Fetch badges for up to 50 teams in one call */
  getTeamBadgesBulk: (teamIds: string[], opts?: { debug?: boolean }) => {
    const params = new URLSearchParams();
    params.set('teamIds', teamIds.join(','));
    if (opts?.debug) params.set('debug', '1');
    return api.get<any[]>(`/scout/badges/bulk?${params.toString()}`);
  },
};

export const profileApi = {
  me: () => api.get<any>('/profile'),
  updateMe: (data: {
    displayName?: string;
    bio?: string;
    avatarUrl?: string;
    socialLinks?: Record<string, string>;
  }) => api.put<any>('/profile', data),
  getPublic: (userId: string) => api.get<any>(`/users/${userId}/profile`),
};

export const adminApi = {
  dashboard: () => api.get<any>('/admin/dashboard'),
  // Users
  getUsers: (params?: { search?: string; page?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    return api.get<any>(`/admin/users?${qs.toString()}`);
  },
  updateUser: (id: string, data: any) => api.patch<any>(`/admin/users/${id}`, data),
  // Brackets
  getBrackets: (params?: { page?: number; limit?: number; userId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.userId) qs.set('userId', params.userId);
    return api.get<any>(`/admin/brackets?${qs.toString()}`);
  },
  getBracketDetail: (id: string) => api.get<any>(`/admin/brackets/${id}`),
  recomputeBracket: (id: string) => api.post<any>(`/admin/scoring/recompute/bracket/${id}`, {}),
  // Analytics
  getAnalytics: (timeRange?: string) =>
    api.get<any>(`/admin/analytics${timeRange ? `?timeRange=${timeRange}` : ''}`),
  // Feature Flags
  getFeatureFlags: () => api.get<any>('/admin/feature-flags'),
  updateFeatureFlag: (key: string, value: boolean) =>
    api.patch<any>(`/admin/feature-flags/${key}`, { value }),
  // Challenges
  getChallenges: () => api.get<any>('/admin/challenges'),
  createChallenge: (data: any) => api.post<any>('/admin/challenges', data),
  updateChallenge: (id: string, data: any) => api.patch<any>(`/admin/challenges/${id}`, data),
};
